import './style.css';
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Dialog, Spinner } from '@economic/taco';

import { t, useLocalization } from '../../../../shared/components/l10n/l10n';
import StepError from './StepError';
import StepOnboard from './StepOnboard';
import StepValidate from './StepValidate';
import MoneyflowOnboardDialog from '../../../sales/invoicing/invoices/book-and-send/moneyflow/always-on-version/MoneyflowOnboardDialog';
import useQuery from '../../../../shared/hooks/useQuery';
import { MoneyFlowSettings } from '../../../settings/integrations/moneyflow/types';
import AcceptTermsAndConditions from './StepAcceptTermsAndConditions';

interface OnboardingPageState {
    startStep?: number;
}
export enum OnboardingStep {
    Error = -1,
    Validate = 0,
    ConfirmStart = 1,
    AcceptTermsAndConditions = 2,
    Onboard = 3,
    EditSettings = 4,
    OnboardingComplete = 5,
}

function MoneyflowOnboardingDialog() {
    const location = useLocation();
    const loadingTexts = useLocalization(textIds);
    const navigate = useNavigate();

    const moneyflowSettingsQuery = useQuery<MoneyFlowSettings>('/moneyflow/settings');

    const startStep = (location.state as OnboardingPageState)?.startStep ?? 0;
    const [step, setStep] = useState(startStep);

    const open = location.hash === '#onboardMoneyflow';

    function hide() {
        if (location.pathname.includes('/sales/invoicing/invoices') && location.state.draftId) {
            navigate(`/sales/invoicing/invoices/${location.state.draftId}`);
            window.location.reload();
        } else {
            navigate(location.pathname);
        }
    }

    function next() {
        setStep(step + 1);
    }

    function error() {
        setStep(-1);
    }

    if (loadingTexts || moneyflowSettingsQuery.loading) {
        return (
            <Dialog open={open} onClose={hide}>
                <Dialog.Content className="flex flex-col items-center" aria-label={t`TOAST_LOADING`}>
                    <Spinner />
                </Dialog.Content>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={hide} closeOnEscape={false} showCloseButton={step !== 3} size="sm">
            <Dialog.Content aria-label="Moneyflow" className="moneyflow__onboarding">
                {step === OnboardingStep.Error && <StepError onClose={hide} />}
                {step === OnboardingStep.Validate && <StepValidate onDone={next} onCancel={hide} />}
                {step === OnboardingStep.ConfirmStart && <MoneyflowOnboardDialog onDone={next} onCancel={hide} />}
                {step === OnboardingStep.AcceptTermsAndConditions && <AcceptTermsAndConditions onDone={next} />}
                {step === OnboardingStep.Onboard && <StepOnboard onError={error} onCancel={hide} />}
            </Dialog.Content>
        </Dialog>
    );
}

const textIds = [
    'TOAST_LOADING',
    'MONEYFLOW_ONBOARDING_CONFIRM_HEADER',
    'MONEYFLOW_ONBOARDING_CONFIRM_TEXT1',
    'MONEYFLOW_ONBOARDING_CONFIRM_TEXT2',
    'MONEYFLOW_ONBOARDING_CONFIRM_SHAREDDATA',
    'MONEYFLOW_ONBOARDING_CONFIRM_START_BUTTON',
    'MONEYFLOW_ONBOARDING_CONFIRM_FREE',
    'MONEYFLOW_ONBOARDING_FOOTER_PROVIDER',
    'MONEYFLOW_ONBOARDING_FOOTER_APPROVER_NAME',
    'MONEYFLOW_ONBOARDING_FOOTER_APPROVER',
];

export default MoneyflowOnboardingDialog;
